import React, { useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import cn from 'classnames';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import RefreshIcon from '@wix/da-ds/pkg/Icons/16x16/Refresh';
import TrashCan from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Flex from '@wix/da-ds/pkg/Flex';
import {
  BiData,
  SubmitReplaceSourceImageOpenBiEvent,
} from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { IMAGE_MIME, VIDEO_MIME } from '@wix/da-shared-react/pkg/utils/mime';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { SubmitFormContext } from '../../../context';
import useAttachmentManager from '../../../hooks/useAttachmentManager';
import SortableAttachmentThumbSlider from '../SortableAttachmentThumbSlider';
import ImagePreview from '../ImagePreview';
import AttachmentPreview from '../AttachmentPreview';
import AttachmentCaptionEditor from '../AttachmentCaptionEditor';
import MainThumb from '../MainThumb';
import DropZoneIndicator from '../DropZoneIndicator';
import { useSubmitFormStatus } from '../../../hooks/useSubmitFormStatus';
import DropZoneProgressBar from '../DropZoneProgressBar';
import {
  updatePcpAttachments,
  removePcpAttachment,
} from '../../../redux/actions';

import s from './MultiImagePreview.scss';

interface Props {
  className?: string;
}

// TODO: Replace this with an instance of MultiImageUploader
const MultiImagePreview: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const { initResponse, refreshInitData, params } =
    useContext(SubmitFormContext)!;
  const { isFormBusy, isMainUploading } = useSubmitFormStatus();
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const deviation = initResponse?.deviation;
  const isVideo = initResponse?.deviation?.isVideo;

  const handleAttachSuccess = useCallback(() => {
    refreshInitData && refreshInitData();
  }, [refreshInitData]);

  const {
    input,
    activeUploads,
    attachments,
    maxAttachmentsAllowed,
    removeAttachment,
    getDropzoneRootProps,
    isDragging,
    isDragAccept,
    isDragReject,
    selectedAttachmentId,
    selectAttachmentId,
  } = useAttachmentManager({ deviation, onSuccess: handleAttachSuccess });

  const handleReplaceSource = useCallback(() => {
    return true;
  }, []);

  const handleReplaceSourceSuccess = useCallback(() => {
    refreshInitData && refreshInitData();
    setValue('addWatermark', false, { shouldDirty: true });
  }, [refreshInitData, setValue]);

  const handleReplaceSourceFailure = useCallback(
    (payload, error) => {
      dispatch(addErrorNotification(error.message));
    },
    [dispatch]
  );

  const {
    input: sourceFileInput,
    activeUploads: activeSourceFileUploads,
    getDropzoneRootProps: getSourceFileRootProps,
  } = useFileUpload({
    id: 'submit-image-replacement',
    allowMultiple: false,
    allowDragAndDrop: false,
    acceptedMimeTypes: isVideo ? VIDEO_MIME : IMAGE_MIME,
    deviationId: params?.deviationId,
    onDrop: handleReplaceSource,
    onSuccess: handleReplaceSourceSuccess,
    onFailure: handleReplaceSourceFailure,
  });

  const attachmentsCount = (attachments || []).length;
  const selectedAttachment = attachments?.find(
    a => a.fileId === selectedAttachmentId
  );

  const handleSliderItemClick = useCallback(
    (attachment: PapiAttachmentFile) => selectAttachmentId(attachment.fileId),
    [selectAttachmentId]
  );

  const handleAttachmentAdded = useCallback(
    (_, updatedDeviation: PapiDeviation) => {
      const additionalMedia = updatedDeviation.extended?.additionalMedia ?? [];
      dispatch(
        updatePcpAttachments(
          additionalMedia.map(media => ({
            ...media,
            isOriginal: true,
          }))
        )
      );
    },
    [dispatch]
  );

  const handleAttachmentRemove = useCallback(
    (fileId: number) => {
      removeAttachment(fileId);
      dispatch(removePcpAttachment(fileId));
    },
    [dispatch, removeAttachment]
  );

  const activeUpload = [...activeSourceFileUploads, ...activeUploads].pop();
  const uploadProgress = activeUpload?.progress;
  const allowUpload = !isFormBusy && !isMainUploading;

  const mainImageView = !selectedAttachment;
  const attachmentView = !!selectedAttachment;

  const renderInfoIconWithTooltip = () => (
    <SimpleTooltip
      aria-label={t('common.learnMore')}
      text={t(
        hasCoreMembership
          ? 'deviationSubmit.form.manageImages.tooltip.core'
          : 'deviationSubmit.form.manageImages.tooltip.nonCore',
        {
          postProcess: 'reactPostprocessor',
          learnMoreLink: (
            <PlainButtonOrLink
              href={t('deviationSubmit.form.manageImages.tooltip.link')}
              target="_blank"
              className={s['learn-more']}
            >
              {t('common.learnMore')}
            </PlainButtonOrLink>
          ),
        }
      )}
      usePortal
    >
      <InfoIcon size={IconSize.SMALLER} disableDefaultColors />
    </SimpleTooltip>
  );

  const renderPreviewContent = () => (
    <div className={s['preview']}>
      {!selectedAttachment && (
        <ImagePreview className={s['preview']} deviation={deviation} />
      )}
      {selectedAttachment && (
        <AttachmentPreview
          key={selectedAttachment.fileId}
          className={s['preview']}
          attachment={selectedAttachment}
        />
      )}
    </div>
  );

  return (
    <Flex direction="column" className={cn(s['root'], className)}>
      <div className={s['header']}>
        {mainImageView && (
          <Flex direction="row" justifyContent="space-between">
            <Flex inline direction="row" alignItems="center" gap={6}>
              <span className={s['attachment-count']}>
                {attachmentsCount + 1}/{maxAttachmentsAllowed + 1} images
              </span>
              {renderInfoIconWithTooltip()}
            </Flex>
            <label {...getSourceFileRootProps()}>
              <TextButton
                className={cn(s['edit-btn'], !allowUpload && s['disabled'])}
                variant="main"
                size="medium"
                icon={RefreshIcon}
                disabled={!allowUpload}
                role="none"
                tabIndex={-1}
                biData={
                  deviation &&
                  BiData<SubmitReplaceSourceImageOpenBiEvent>({
                    evid: 31,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                  })
                }
              >
                {t('deviationSubmit.form.imageReplace.button.text')}
              </TextButton>
              {allowUpload && sourceFileInput}
            </label>
          </Flex>
        )}
        {attachmentView && (
          <Flex direction="row" justifyContent="space-between">
            <Flex inline direction="row" alignItems="center" gap={6}>
              <strong className={s['attachment-count']}>
                {attachmentsCount + 1}/{maxAttachmentsAllowed + 1} images
              </strong>
              {renderInfoIconWithTooltip()}
            </Flex>

            <TextButton
              variant="main"
              size="medium"
              icon={TrashCan}
              disabled={!allowUpload}
              onClick={() => removeAttachment(selectedAttachmentId!)}
            >
              {t('deviationSubmit.form.removeAttachment.button.text')}
            </TextButton>
          </Flex>
        )}
      </div>

      <div
        className={s['content']}
        {...(allowUpload ? getDropzoneRootProps() : {})}
      >
        <Flex alignItems="center" justifyContent="center" fullHeight>
          {isMainUploading && (
            <div className={s['uploading-wrapper']}>
              <DropZoneProgressBar
                className={s['uploading']}
                label={t('uploadZone.progress.title')}
                progress={uploadProgress}
              />
            </div>
          )}
          {allowUpload ? (
            <DropZoneIndicator
              className={s['dropzone']}
              isDragActive={isDragging}
              isDragAccept={isDragAccept}
              isDragReject={isDragReject}
            >
              {renderPreviewContent()}
            </DropZoneIndicator>
          ) : (
            renderPreviewContent()
          )}
          {input}
        </Flex>
      </div>

      {deviation && attachmentsCount > 0 && (
        <div className={s['caption-input-wrapper']}>
          <AttachmentCaptionEditor
            key={selectedAttachmentId}
            deviation={deviation}
            attachment={selectedAttachment}
            placeholder={t('deviationSubmit.form.imageReplace.caption.label')}
          />
        </div>
      )}

      <div className={s['footer']}>
        <Flex direction="row">
          {deviation && (
            <MainThumb
              className={s['main-thumb']}
              item={deviation}
              onClick={() => selectAttachmentId(undefined)}
              selected={!selectedAttachment}
            />
          )}
          <div className={s['separator']} />
          <SortableAttachmentThumbSlider
            className={s['thumbs-slider']}
            deviation={deviation}
            selectedItemId={selectedAttachmentId}
            onItemClick={handleSliderItemClick}
            isUploading={isMainUploading}
            removeAttachment={handleAttachmentRemove}
            onAttachmentAdded={handleAttachmentAdded}
          />
        </Flex>
      </div>
    </Flex>
  );
};
MultiImagePreview.displayName = 'MultiImagePreview';

export default MultiImagePreview;
